import type {
    IndexedDevices,
    InputConstraintSet,
    InputDeviceConstraint,
    MediaDeviceInfoLike,
    MediaDeviceRequest,
} from '@pexip/media-control';
import type {RendererOptions, RenderEffects} from '@pexip/media-processor';
import type {Signal} from '@pexip/signal';

export type Unsubscribe = () => void;

export interface ExtendedMediaTrackSettings extends MediaTrackSettings {
    mixWithAdditionalMedia?: boolean;
    channelCount?: number;
    denoise?: boolean;
    vad?: boolean;
    asd?: boolean;
    backgroundBlurAmount?: number;
    backgroundImageUrl?: string;
    edgeBlurAmount?: number;
    maskCombineRatio?: number;
    foregroundThreshold?: number;
    videoSegmentation?: RenderEffects;
    panEnabled?: boolean;
    tiltEnabled?: boolean;
    zoomEnabled?: boolean;
    pan?: number;
    tilt?: number;
    zoom?: number;
    contentHint?: AudioContentHint | VideoContentHint;
}

export type ExtendedMediaTrackSettingsKey = keyof ExtendedMediaTrackSettings;

export interface MediaSettings {
    audio?: ExtendedMediaTrackSettings;
    video?: ExtendedMediaTrackSettings;
}

/**
 * URL links needed for creating a denoise node for audio processing
 */
export interface DenoiseParams {
    /**
     * wasm URL to get the denoise wasm
     */
    wasmURL: string;
    /**
     * AudioWorklet module URL to get the worklet, @see AudioContext['audioWorklet']['addModule']
     */
    workletModule: string;
    /**
     * AudioWorklet options to pass @see AudioContext['audioWorklet']['addModule']
     */
    workletOptions?: WorkletOptions;
}

export interface MediaAttributes {
    /**
     * Indicating whether the media is active
     *
     * A stream is considered active if at least one of its MediaStreamTracks
     * does not have its property MediaStreamTrack.readyState set to ended.
     * Once every track has ended, the stream's active property becomes false.
     */
    readonly active: boolean;

    /**
     * A string containing a 36-character universally unique identifier (UUID)
     * for the object.
     */
    readonly id: string;
    /**
     * The devices used for the media
     */
    devices: IndexedDevices;
    /**
     * Media stream for the media
     */
    stream: MediaStream;
    /**
     * The audio input device which is expected to be used for the current
     * stream based on the provided constraints
     */
    expectedAudioInput?: MediaDeviceInfoLike;
    /**
     * The video input device which is expected to be used for the current
     * stream based on the provided constraints
     */
    expectedVideoInput?: MediaDeviceInfoLike;
    /**
     * The status of the media
     */
    status: UserMediaStatus;
    /**
     * Current mute state of audio track
     * `undefined` means there is no such track from the stream
     */
    audioMuted: boolean | undefined;
    /**
     * Current mute state of video track
     * `undefined` means there is no such track from the stream
     */
    videoMuted: boolean | undefined;
    /**
     * Audio input device is used for the audio track from the current stream
     */
    audioInput?: MediaDeviceInfoLike;
    /**
     * Video input device is used for the video track from the current stream
     */
    videoInput?: MediaDeviceInfoLike;
}

export interface Media extends MediaAttributes {
    /**
     * mute/unmute the audio track
     */
    muteAudio(mute: boolean): void;
    /**
     * mute/unmute the video track
     */
    muteVideo(mute: boolean): void;
    /**
     * Apply the constraints to the current media
     */
    applyConstraints(constraints: MediaDeviceRequest): Promise<void>;
    /**
     * Release the media resources, e.g. camera/microphone
     */
    release(shouldEmit?: () => boolean): Promise<void>;
    getConstraints(): MediaDeviceRequest;
    setOriginalConstraints(constraints: MediaDeviceRequest): void;
    getOriginalConstraints(): MediaDeviceRequest;
    isAudioInputUnavailable(): boolean;
    isVideoInputUnavailable(): boolean;
    requestedAudio(): boolean;
    requestedVideo(): boolean;
    getSettings(): MediaSettings;
    toJSON(): unknown;
    getTracks(): MediaTrack[];
    getAudioTracks(): MediaTrack[];
    getVideoTracks(): MediaTrack[];
    addTrack(track: MediaTrack): void;
    isCurrentTrack(track: MediaTrack): boolean;
    removeTrack(track: MediaTrack): void;
    clone(signals?: MediaSignals, label?: string): Media;
}

export interface MediaInit {
    constraints: MediaDeviceRequest;
    permission: {audio: PermissionState; video: PermissionState};
    originalConstraints?: MediaDeviceRequest;
    tracks: MediaTrack[];
    devices: IndexedDevices;
    status: UserMediaStatus;
    stream: MediaStream | undefined;
    signals?: MediaSignals;
}

export interface MediaTrack {
    /**
     * A unique identifier for the track same as `MediaStreamTrack.id` if
     * `track` is available otherwise it uses the `id` from the construction or the
     * `kind`. @see MediaTrackInit
     */
    readonly id: string;
    /**
     * It maps to MediaStreamTrack['readyState']
     * When `true` the track's `readyState` is `ended`, `false` means `live` otherwise the track is not available
     */
    readonly stopped?: boolean;
    /**
     * Indicate that the media has been suspended by the User Agent, and it could happen at any time.
     * `undefined` means the track is not available
     */
    readonly suspended?: boolean;
    readonly kind: 'audioinput' | 'videoinput';
    readonly label?: string;
    readonly previousMediaTrack?: MediaTrack;
    /**
     * The MediaStreamTrack object representing the track
     * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack
     */
    readonly track?: MediaStreamTrack;
    /**
     * The `MediaDeviceInfo` object representing the device that the track is
     * connected to. @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDeviceInfo
     */
    readonly input?: MediaDeviceInfoLike;
    /**
     * The `MediaDeviceInfo` object representing the device that the track is
     * expected to be connected to. @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDeviceInfo
     */
    readonly expectedInput?: MediaDeviceInfoLike;
    /**
     * The current mute state of the track. `undefined` means the track is not available
     */
    muted?: boolean;
    /**
     * The source track that it is originated from
     */
    readonly source: MediaTrack;

    /**
     * Mute the track
     *
     * @param toMute - `true` to mute the track, `false` to unmute
     * @param soft - `true` to indicate a soft mute i.e. setting the track to NOT `enabled`
     * `false` or `undefined` for default mute
     */
    mute(toMute: boolean, soft?: boolean): void;
    getSettings(): ExtendedMediaTrackSettings;
    getConstraints(): InputDeviceConstraint;
    applyConstraints(constraints: InputDeviceConstraint): Promise<void>;
    release(shouldEmit?: () => boolean): Promise<void>;
    clone(signals?: MediaSignals, label?: string): MediaTrack;
    toJSON(): unknown;
}

export interface MediaTrackInit extends Partial<Omit<MediaTrack, 'mute'>> {
    kind: 'audioinput' | 'videoinput';
    input: MediaDeviceInfoLike | undefined;
    expectedInput?: MediaDeviceInfoLike | undefined;
    constraints: InputDeviceConstraint;
    overrideMute?: boolean;
    signals?: MediaSignals;
    /**
     * Overriding Mute the track
     *
     * @param toMute - `true` to mute the track, `false` to unmute
     * @param self - The media track itself
     * @param soft - `true` to indicate a soft mute i.e. setting the track to NOT `enabled`
     * `false` or `undefined` for default mute
     */
    mute?: (toMute: boolean, self: MediaTrack, soft?: boolean) => void;
}

export type Process<T> = (a: T) => Promise<MediaTrack>;
export type TrackProcessor = Process<MediaTrack>;

/** Acquires media and applies it to the current media state. */
export type GetUserMediaProcess = (request: {
    /** Constraints for this acquisition attempt. */
    constraints: MediaDeviceRequest;
    /** Constraints before any acquisition fallback is applied. */
    originalConstraints: MediaDeviceRequest;
    /** Known browser permission state for each input. */
    permission: {audio: PermissionState; video: PermissionState};
    /** Media state to update, when one already exists. */
    currentMedia: Media | undefined;
    /** Returns whether the request result is still current. */
    isCurrent?: () => boolean;
    /** Cancels the acquisition when aborted. */
    signal?: AbortSignal;
}) => Promise<Media>;

/**
 * Bit flags describing the lifecycle and terminal state of a media input.
 */
export enum MediaInputStatusFlag {
    /** No known status. */
    None = 0,
    /** The input was included in the media request. */
    Requested = 1 << 0,
    /** Browser permission has not yet been decided. */
    PermissionPrompt = 1 << 1,
    /** Browser permission was granted. */
    PermissionGranted = 1 << 2,
    /** Browser permission was denied. */
    PermissionDenied = 1 << 3,
    /** At least one device is available for the input. */
    DeviceAvailable = 1 << 4,
    /** The acquired stream contains a track for the input. */
    TrackPresent = 1 << 5,
    /** The acquired track exactly matches the requested device. */
    ExactMatch = 1 << 6,
    /** A fallback device was acquired instead of the requested device. */
    Fallback = 1 << 7,
    /** No device is available for the input. */
    NoDevice = 1 << 8,
    /** The specifically requested device was not found. */
    RequestedDeviceNotFound = 1 << 9,
    /** The device could not start, typically because it is already in use. */
    DeviceInUse = 1 << 10,
    /** The requested constraints cannot be satisfied. */
    Overconstrained = 1 << 11,
    /** The supplied constraints are invalid. */
    InvalidConstraints = 1 << 12,
    /** User-media acquisition is unsupported. */
    NotSupported = 1 << 13,
    /** User-media acquisition failed for an unknown reason. */
    UnknownError = 1 << 14,
    /** User-media acquisition exceeded its timeout. */
    TimedOut = 1 << 15,
}

/** Bit masks for extracting status categories from a media input status. */
export const InputStatusMask = {
    Permission: 14,
    Match: 192,
    Error: 65280,
    Known: 65535,
} as const;

/** A bit mask value used to select a media input status category. */
export type InputStatusMask =
    (typeof InputStatusMask)[keyof typeof InputStatusMask];

/** A bit field composed of {@link MediaInputStatusFlag} values. */
export type MediaInputStatus = number;

/** Status of the audio and video inputs for a user-media request. */
export interface UserMediaStatus {
    /** Audio input status flags. */
    readonly audio: MediaInputStatus;
    /** Video input status flags. */
    readonly video: MediaInputStatus;
}

/** A user-media input category. */
export type MediaInputKind = 'audio' | 'video';

const hasAtMostOneBit = (value: number) =>
    value === 0 || (value & (value - 1)) === 0;

export const isMediaInputStatus = (
    value: unknown,
): value is MediaInputStatus => {
    if (
        typeof value !== 'number' ||
        !Number.isInteger(value) ||
        value < 0 ||
        value > InputStatusMask.Known ||
        (value & ~InputStatusMask.Known) !== 0
    ) {
        return false;
    }
    const match = value & InputStatusMask.Match;
    const hasTrack = (value & MediaInputStatusFlag.TrackPresent) !== 0;
    const requested = (value & MediaInputStatusFlag.Requested) !== 0;
    return (
        hasAtMostOneBit(value & InputStatusMask.Permission) &&
        hasAtMostOneBit(value & InputStatusMask.Match) &&
        hasAtMostOneBit(value & InputStatusMask.Error) &&
        (match === 0 || (hasTrack && requested))
    );
};

const assertMediaInputStatus = (value: number) => {
    if (!isMediaInputStatus(value)) {
        throw new Error(`Invalid media input status: ${value}`);
    }
};

export const createUserMediaStatus = ({
    audio = MediaInputStatusFlag.None,
    video = MediaInputStatusFlag.None,
}: Partial<UserMediaStatus> = {}): UserMediaStatus => {
    assertMediaInputStatus(audio);
    assertMediaInputStatus(video);
    return Object.freeze({audio, video});
};

export const EMPTY_USER_MEDIA_STATUS = createUserMediaStatus();

const MEDIA_INPUT_STATUS_FLAGS = [
    ['Requested', MediaInputStatusFlag.Requested],
    ['PermissionPrompt', MediaInputStatusFlag.PermissionPrompt],
    ['PermissionGranted', MediaInputStatusFlag.PermissionGranted],
    ['PermissionDenied', MediaInputStatusFlag.PermissionDenied],
    ['DeviceAvailable', MediaInputStatusFlag.DeviceAvailable],
    ['TrackPresent', MediaInputStatusFlag.TrackPresent],
    ['ExactMatch', MediaInputStatusFlag.ExactMatch],
    ['Fallback', MediaInputStatusFlag.Fallback],
    ['NoDevice', MediaInputStatusFlag.NoDevice],
    ['RequestedDeviceNotFound', MediaInputStatusFlag.RequestedDeviceNotFound],
    ['DeviceInUse', MediaInputStatusFlag.DeviceInUse],
    ['Overconstrained', MediaInputStatusFlag.Overconstrained],
    ['InvalidConstraints', MediaInputStatusFlag.InvalidConstraints],
    ['NotSupported', MediaInputStatusFlag.NotSupported],
    ['UnknownError', MediaInputStatusFlag.UnknownError],
    ['TimedOut', MediaInputStatusFlag.TimedOut],
] as const;

const formatMediaInputStatus = (status: MediaInputStatus) => {
    const flags = MEDIA_INPUT_STATUS_FLAGS.flatMap(([name, flag]) =>
        (status & flag) === flag ? [name] : [],
    );
    return flags.length ? flags : ['None'];
};

export const formatUserMediaStatus = (status: UserMediaStatus) => ({
    audio: formatMediaInputStatus(status.audio),
    video: formatMediaInputStatus(status.video),
});

export const hasInputStatus = (
    status: UserMediaStatus,
    kind: MediaInputKind,
    flags: MediaInputStatusFlag,
) => (status[kind] & flags) === flags;

export const updateInputStatus = (
    status: UserMediaStatus,
    kind: MediaInputKind,
    value: MediaInputStatus,
): UserMediaStatus => {
    assertMediaInputStatus(value);
    if (status[kind] === value) {
        return status;
    }
    return createUserMediaStatus({...status, [kind]: value});
};

export const replaceInputStatus = (
    status: UserMediaStatus,
    kind: MediaInputKind,
    mask: InputStatusMask,
    value: MediaInputStatus,
) => {
    if ((value & ~mask) !== 0) {
        throw new Error(`Status ${value} is outside mask ${mask}`);
    }
    return updateInputStatus(status, kind, (status[kind] & ~mask) | value);
};

export const areUserMediaStatusesEqual = (
    first: UserMediaStatus,
    second: UserMediaStatus,
) => first.audio === second.audio && first.video === second.video;

export interface AudioSignalDetectionOptions {
    /**
     * Audio Signal Detection Duration in second
     */
    audioSignalDetectionDuration?: number;
    /**
     * Whether or not to detect audio signal for malfunctioning device
     */
    shouldDetectAudio: () => boolean;
}

export interface VoiceActivityDetectionOptions {
    /**
     * Voice Activity Detection in millisecond
     */
    vadThrottleMS?: number;
    /**
     * Whether or not to detect voice activity
     */
    shouldDetectVoiceActivity: () => boolean;
}

interface DeviceMuteState {
    /**
     * Indicating whether audio is muted
     */
    audio: boolean;
    /**
     * Indicating whether video is muted
     */
    video: boolean;
}

export interface MediaOptions {
    /**
     * Media signals to be used for the module
     *
     * @see MediaSignals
     */
    signals: MediaSignals;
    /**
     * Media Processors
     */
    audioProcessors: TrackProcessor[];
    videoProcessors: TrackProcessor[];
    stopVideoTrackAsMute?: () => boolean;
    /**
     * A function to get the devices' mute state
     */
    getMuteState: () => DeviceMuteState;
    /**
     * Pass default constraints to use with get media wrappers
     */
    getDefaultConstraints?: () => {
        audio?: InputConstraintSet | false;
        video?: InputConstraintSet | false;
    };
}

export interface MediaProps {
    /**
     * Current media
     */
    media?: Media;
    /**
     * Current device
     */
    devices: IndexedDevices;
    updatingAudio: boolean;
    updatingVideo: boolean;
}

export interface MediaController {
    readonly updatingAudio: boolean;
    readonly updatingVideo: boolean;
    /**
     * Current Media
     */
    readonly media?: Media;
    /**
     * Current device list
     */
    readonly devices: IndexedDevices;
    /**
     * Queue a media pipeline update with the provided constraints.
     *
     * Requests run serially. A request that becomes stale or times out cannot
     * overwrite the current media, device list, or input status.
     *
     * @param constraints - @see MediaDeviceRequest
     */
    getUserMedia: (constraints: MediaDeviceRequest) => void;
    /**
     * Queue a media pipeline update with the provided constraints and resolve
     * after that update has completed. This explicit version of `getUserMedia`
     * makes it possible to chain other async actions.
     *
     * Requests run serially. A request that becomes stale or times out cannot
     * overwrite the current media, device list, or input status.
     *
     * @param constraints - @see MediaDeviceRequest
     */
    getUserMediaAsync: (constraints: MediaDeviceRequest) => Promise<void>;
    /**
     * Queue a media update after cross-checking PermissionState and available
     * devices. The request is skipped when there is no granted device.
     *
     * A stale permission check cannot overwrite a newer media request.
     *
     * @param constraints - @see MediaDeviceRequest
     */
    tryAndGetUserMedia: (constraints: MediaDeviceRequest) => void;
}

export type VideoRenderParams = Omit<
    RendererOptions,
    'effects' | 'personCenter'
> & {
    /**
     * Target frame rate for the video segmentation
     */
    frameRate: number;
    /**
     * Render Effects
     */
    width: number;
    height: number;
    videoSegmentation: RenderEffects;
    pan?: boolean;
    tilt?: boolean;
    zoom?: boolean;

    personCenterX?: number;
    personCenterY?: number;
};

export interface MediaChangesSignals {
    onAddTrack: Signal<MediaStreamTrack>;
    onDevicesChanged: Signal<IndexedDevices>;
    onMediaChanged: Signal<Media | undefined>;
    onMediaTrackMuted: Signal<MediaTrack>;
    onMediaTrackResumed: Signal<MediaTrack>;
    onMediaTrackStopped: Signal<MediaTrack>;
    onMediaTrackSuspended: Signal<MediaTrack>;
    onRemoveTrack: Signal<MediaStreamTrack>;
    onStatusChanged: Signal<UserMediaStatus>;
    onUpdatingAudio: Signal<boolean>;
    onUpdatingVideo: Signal<boolean>;
}

export interface AudioDetectionSignals {
    onVAD: Signal<undefined>;
    onSilentDetected: Signal<boolean>;
}

export type MediaSignalsOptional = Omit<
    Partial<MediaChangesSignals>,
    'onMediaChanged'
>;
export type MediaSignalsRequired = Pick<MediaChangesSignals, 'onMediaChanged'> &
    AudioDetectionSignals;

export type MediaSignals = MediaSignalsRequired & MediaSignalsOptional;

export enum DeniedDevices {
    Microphone = 'microphone',
    Camera = 'camera',
    Both = 'microphone-and-camera',
}

/**
 * Audio content hints are only applicable when the MediaStreamTrack contains an
 * audio track
 *
 * {@link https://www.w3.org/TR/mst-content-hint/#audio-content-hints}
 */
export const AUDIO_CONTENT_HINTS = {
    /**
     * No hint has been provided, the implementation should make its
     * best-informed guess on how to handle contained audio data. This may be
     * inferred from how the track was opened or by doing content analysis
     */
    NoHint: '',
    /**
     * The track should be treated as if it contains speech data. Consuming this
     * signal it may be appropriate to apply noise suppression or boost
     * intelligibility of the incoming signal.
     */
    Speech: 'speech',
    /**
     * The track should be treated as if it contains data for the purpose of
     * speech recognition by a machine. Consuming this signal it may be
     * appropriate to boost intelligibility of the incoming signal for
     * transcription and turn off audio-processing components that are used for
     * human consumption.
     */
    SpeechRecognition: 'speech-recognition',
    /**
     * The track should be treated as if it contains music data. Generally this
     * might imply tuning or turning off audio-processing components that are
     * used to process speech data to prevent the audio from being distorted.
     */
    Music: 'music',
} as const;

export type AudioContentHint =
    (typeof AUDIO_CONTENT_HINTS)[keyof typeof AUDIO_CONTENT_HINTS];

/**
 * Video content hints are only applicable when the MediaStreamTrack contains a
 * video track.
 *
 * {@link https://www.w3.org/TR/mst-content-hint/#video-content-hints}
 */
export const VIDEO_CONTENT_HINTS = {
    /**
     * No hint has been provided, the implementation should make its
     * best-informed guess on how contained video content should be treated.
     * This can for example be inferred from how the track was opened or by
     * doing content analysis.
     */
    NoHint: '',
    /**
     * The track should be treated as if it contains video where motion is
     * important. This is normally webcam video, movies or video games.
     * Quantization artefacts and downscaling are acceptable in order to
     * preserve motion as well as possible while still retaining target
     * bitrates. During low bitrates when compromises have to be made, more
     * effort is spent on preserving frame rate than edge quality and details.
     */
    Motion: 'motion',
    /**
     * The track should be treated as if video details are extra important.
     * This is generally applicable to presentations or web pages with text
     * content, painting or line art. This setting would normally optimize for
     * detail in the resulting individual frames rather than smooth playback.
     * Artefacts from quantization or downscaling that make small text or line
     * art unintelligible should be avoided.
     */
    Detail: 'detail',
    /**
     * The track should be treated as if video details are extra important, and
     * that significant sharp edges and areas of consistent color can occur
     * frequently. This is generally applicable to presentations or web pages
     * with text content. This setting would normally optimize for detail in the
     * resulting individual frames rather than smooth playback, and may take
     * advantage of encoder tools that optimize for text rendering. Artefacts
     * from quantization or downscaling that make small text or line art
     * unintelligible should be avoided.
     */
    Text: 'text',
} as const;

export type VideoContentHint =
    (typeof VIDEO_CONTENT_HINTS)[keyof typeof VIDEO_CONTENT_HINTS];
